/**
 * プロンプト試し打ちの検査結果を、用途ごとの本番の扱いに沿って並べ直す（課題 #62）。
 *
 * 試し打ち（POST /v1/admin/prompts/preview）は、用途にかかわらず月次と同じ 4 項目を
 * 検査して返す。一方で本番は用途ごとに行う検査が違う。そのまま見せると
 * 「日次も本番で同じ基準で検査される」と誤解させる（高尾さん 2026-09-24）。
 *
 * 本番で行っている検査は BE main（2026-09-25 時点）の実装に合わせている。
 *   日次      app/worker/daily_reports.py               使えない表現のみ。該当すると書き直さず定型文へ
 *            → BE PR #132（2026-09-29 高尾さん決定）以降は、数値など（D6）・使えない表現・
 *              当日以外と比べる言い方の 3 つ。1 度書き直し、それでも落ちれば定型文へ。
 *              試し打ちの応答に comparison_expressions があるかで、どちらの BE かを見分ける
 *   週次      app/services/weekly_report.py             開示しない内容・使えない表現・判定ではない旨の一文。
 *                                                        1 度書き直し、それでも落ちれば定型文へ
 *   月次      app/services/monthly_report.py            週次の 3 項目＋算出状況との矛盾。扱いは週次と同じ
 *   健康経営  app/services/health_management_report.py  生成後の検査なし
 *
 * 「使えない表現」（REG-01。prohibited_claims）の中身は、BE PR #140（#71 の判定規則表。
 * 2026-09-29 高尾さん了承）で次のように細かくなった。検査の項目・用途ごとの扱いは変わらない。
 *   R-01  逆接（〜ますが・しかし・ただし 等）で節を区切り、節ごとに判定する
 *   R-02  同等性の主張（医療機器と同等・同じ精度・医療機器並み 等）を止める      → REG-01-B
 *   R-03  否定による免責は、同じ節の、検出した語より後ろ・逆接より前に限る
 *   R-04  二重否定（〜とは限りません 等）の否定語は免責に数えない
 *   R-05  「医療機器」の語が無い薬事の主張（薬機法・承認番号・PMDA 等）を止める → REG-01-B
 *   R-06  受診を促す表現（受診・通院・服薬・心療内科 等の命令・推奨）を止める。
 *         否定では免責しない。「相談」は対象外                                     → REG-01-B
 *   R-07  日次・週次・月次・アドバイス・試し打ちは、禁止語の置換の「前」の原文で判定し、
 *         通った文面に置換をかけて採用する（置換で素通りしていた違反も止まる）
 * R-02・R-05・R-06 は既存の規則 ID REG-01-B で返るため、画面では同じ「使えない表現」として出す。
 * 健康経営レポートは生成後の検査が無いので、R-01〜R-07 の対象外（R-07 の 5 か所に入っていない）。
 *
 * 指定した文字列の検査（課題 #62 / BE PR #170。2026-10-19 反映）: 日次・週次で、高尾さんが一覧で指定した
 * 文字列のうち、扱いが「有効」のまとまりに当たると書き直しになる（phrase_rules。passed も false）。
 * 「記録だけ」のまとまりは件数を数えるだけで本番の文面は変えない（phrase_records。passed に影響しない）。
 *
 * 本番の検査を変えるときは、ここも合わせて直す。
 */
import type { PromptPreviewChecks } from '../api/client';

export type PreviewCheckKey =
  | 'nonDiagnosticMissing'
  | 'contradictsMcs'
  | 'leaksInternal'
  | 'prohibitedClaims'
  | 'comparisonExpressions'
  | 'phraseRules';

export interface ProductionPolicy {
  /** BE の workflow_type（正規値） */
  workflowType: 'daily_report' | 'weekly_report' | 'monthly_report' | 'health_management';
  /** 本番で生成後に行う検査 */
  checks: readonly PreviewCheckKey[];
  /** 本番で行う検査に落ちたときの扱い。検査が無ければ null */
  onFail: 'retry' | 'template' | null;
}

const POLICIES: readonly ProductionPolicy[] = [
  { workflowType: 'daily_report', checks: ['prohibitedClaims'], onFail: 'template' },
  {
    workflowType: 'weekly_report',
    checks: ['leaksInternal', 'prohibitedClaims', 'nonDiagnosticMissing', 'phraseRules'],
    onFail: 'retry',
  },
  {
    workflowType: 'monthly_report',
    checks: ['contradictsMcs', 'leaksInternal', 'prohibitedClaims', 'nonDiagnosticMissing'],
    onFail: 'retry',
  },
  { workflowType: 'health_management', checks: [], onFail: null },
];

/** BE PR #132 以降の日次（数値など・使えない表現・当日以外と比べる言い方。1 度書き直し→定型文） */
const DAILY_V2: ProductionPolicy = {
  workflowType: 'daily_report',
  checks: ['leaksInternal', 'prohibitedClaims', 'comparisonExpressions', 'phraseRules'],
  onFail: 'retry',
};

/**
 * 用途（workflow_type）から本番の扱いを引く。
 * FE の用途は BE の正式な値にそろえてあるが（課題 #59）、旧い値（daily_report_comment 等）が
 * 来ても、BE の API 層（_validate_workflow_type）と同じく正式な値の前方一致で寄せる。
 */
export function productionPolicyFor(
  workflowKey: string | null | undefined,
  opts: { dailyChecksV2?: boolean } = {}
): ProductionPolicy | null {
  if (!workflowKey) return null;
  const longestFirst = [...POLICIES].sort((a, b) => b.workflowType.length - a.workflowType.length);
  const policy = longestFirst.find((p) => workflowKey.startsWith(p.workflowType)) ?? null;
  if (policy?.workflowType === 'daily_report' && opts.dailyChecksV2) return DAILY_V2;
  return policy;
}

export interface FailedCheck {
  key: PreviewCheckKey;
  /** 該当した語など（無ければ空） */
  items: string[];
}

/** 落ちた検査を固定の順で並べる */
export function failedChecks(checks: PromptPreviewChecks): FailedCheck[] {
  const leaks = checks.leaks_internal_values ?? [];
  const claims = checks.prohibited_claims ?? [];
  const out: FailedCheck[] = [];
  if (checks.non_diagnostic_note_missing) out.push({ key: 'nonDiagnosticMissing', items: [] });
  if (checks.contradicts_mcs_availability) out.push({ key: 'contradictsMcs', items: [] });
  if (leaks.length > 0) out.push({ key: 'leaksInternal', items: leaks });
  if (claims.length > 0) out.push({ key: 'prohibitedClaims', items: claims });
  const comparisons = checks.comparison_expressions ?? [];
  if (comparisons.length > 0) out.push({ key: 'comparisonExpressions', items: comparisons });
  const phrases = checks.phrase_rules ?? [];
  if (phrases.length > 0) out.push({ key: 'phraseRules', items: phrases });
  return out;
}

/**
 * 指定した文字列の当たりを、まとまりごとにまとめて「「2」「3」（digit）、「最近」（compare_other_days）」の形にする。
 * BE は `まとまり:文字列` で返す。最初の「:」で分ける（まとまりの名前に「:」は使わない。BE 2026-10-19）。
 * 同じまとまりの同じ文字列は 1 回だけ出す。まとまりの名前は、高尾さんの一覧と同じ英語の名前のまま出す
 * （日本語の名前を FE で持つと、一覧が変わるたびに直すことになるため）
 */
export function formatPhraseHits(hits: readonly string[]): string {
  const groups = new Map<string, string[]>();
  for (const hit of hits) {
    const i = hit.indexOf(':');
    const group = i > 0 ? hit.slice(0, i) : '';
    const phrase = i > 0 ? hit.slice(i + 1) : hit;
    const phrases = groups.get(group) ?? [];
    if (!phrases.includes(phrase)) phrases.push(phrase);
    groups.set(group, phrases);
  }
  return [...groups]
    .map(([group, phrases]) => phrases.map((p) => `「${p}」`).join('') + (group ? `（${group}）` : ''))
    .join('、');
}

export interface PreviewCheckSummary {
  policy: ProductionPolicy | null;
  /** 本番でも行う検査のうち落ちたもの。用途が分からないときは落ちたもの全部 */
  failedInProduction: FailedCheck[];
  /** 本番のこの用途では行わない検査のうち落ちたもの（参考） */
  failedPreviewOnly: FailedCheck[];
  /**
   * 本番での扱い。
   * pass: 本番で行う検査にはすべて通る / retry: 1 度書き直し→定型文 / template: 書き直さず定型文
   * unknown: 用途が分からず、落ちた検査があるのに本番での扱いを言えない
   */
  outcome: 'pass' | 'retry' | 'template' | 'unknown';
  /**
   * 「記録だけ」のまとまりで見つかった文字列（課題 #62 / BE PR #170）。本番の文面は変えず件数だけを数えるので、
   * 検査の合否（outcome）には入れない
   */
  recordedPhrases: string[];
}

export function summarizePreviewChecks(
  checks: PromptPreviewChecks,
  workflowKey: string | null | undefined
): PreviewCheckSummary {
  // 応答に comparison_expressions があれば、日次の生成後の検査に対応した BE（PR #132 以降）
  const policy = productionPolicyFor(workflowKey, {
    dailyChecksV2: Array.isArray(checks.comparison_expressions),
  });
  const failed = failedChecks(checks);
  const recordedPhrases = checks.phrase_records ?? [];
  if (!policy) {
    return {
      policy: null,
      failedInProduction: failed,
      failedPreviewOnly: [],
      outcome: failed.length > 0 ? 'unknown' : 'pass',
      recordedPhrases,
    };
  }
  const failedInProduction = failed.filter((f) => policy.checks.includes(f.key));
  const failedPreviewOnly = failed.filter((f) => !policy.checks.includes(f.key));
  return {
    policy,
    failedInProduction,
    failedPreviewOnly,
    outcome: failedInProduction.length > 0 && policy.onFail ? policy.onFail : 'pass',
    recordedPhrases,
  };
}
